import { drawRoutineOrbit } from '../../../../engine/core/routine-orbit.js';
/**
 * Mindmap — Canvas Renderer (mobile).
 *
 * Same approach as the bubble canvas renderer: instead of building one <div>
 * per node plus an SVG path per edge (~1500 elements on a 715-node map, the
 * dominant cost of every mobile render), the whole map is painted onto a
 * single viewport-sized <canvas>.
 *
 * The scene is built by the SAME layout pass the DOM renderer uses —
 * render.js collects paint records instead of DOM nodes and lays them out
 * with tidy-layout.js, so positions and sides are identical by
 * construction. Painting is dirty-driven via markDirty(); there is no
 * standing rAF loop (the mindmap has no physics).
 *
 * Feature scope on canvas (mobile): colored rounded-rect nodes with white
 * wrapped/ellipsised titles, gradient-free colored edges, collapsed "+"
 * hint, notes dot, done-status strikethrough, tag-spotlight dimming,
 * tap-to-select hit-testing. Desktop keeps the DOM renderer (drag,
 * inline rename, context menus, hover previews).
 */

import { safeColor } from "../../../../engine/core/validation.js";
import { t } from "../../../../shared/i18n/index.js";

let canvasEl = null;
let ctx = null;
let host = null;
let getTransformFn = null;
let getSelectedIdFn = null;
let onTapFn = null;
let onLongPressFn = null;
let rafId = null;
let resizeObserver = null;
let themeObserver = null;
let dirty = false;
let scene = { nodes: [], edges: [] };
// Selection uses the app-wide accent (--accent-cyan) so it matches the
// sidebar tree, the outline highlight and the bubble canvas.
let themeAccent = "#00d4aa";
let themeOrbit = "#8b949e";

const DPR_CAP = 1.5;

export function mount(opts) {
  if (canvasEl) return;
  host = opts.host;
  getTransformFn = opts.getTransform;
  getSelectedIdFn = opts.getSelectedId || (() => null);
  onTapFn = opts.onTap;
  onLongPressFn = opts.onLongPress || null;

  canvasEl = document.createElement("canvas");
  canvasEl.className = "mm-canvas-overlay";
  canvasEl.style.cssText = [
    "position: absolute",
    "left: 0",
    "top: 0",
    "width: 100%",
    "height: 100%",
    "pointer-events: auto",
    "touch-action: none",
    "z-index: 2",
  ].join(";");
  host.appendChild(canvasEl);
  ctx = canvasEl.getContext("2d");

  updateTheme();
  themeObserver = new MutationObserver(() => { updateTheme(); markDirty(); });
  themeObserver.observe(document.documentElement, {attributes: true, attributeFilter: ['data-theme']});

  resize();
  if (typeof ResizeObserver !== "undefined") {
    resizeObserver = new ResizeObserver(() => { resize(); markDirty(); });
    resizeObserver.observe(host);
  } else {
    window.addEventListener("resize", onWindowResize);
  }

  canvasEl.addEventListener("pointerdown", onPointerDown);
  canvasEl.addEventListener("pointermove", onPointerMove);
  canvasEl.addEventListener("pointerup", onPointerUp);
  canvasEl.addEventListener("pointercancel", onPointerCancel);
  // Long-press is ours; keep the browser's context menu / text callout out.
  canvasEl.addEventListener("contextmenu", onContextMenu);

  markDirty();
}

function updateTheme() {
  const style = getComputedStyle(host);
  themeOrbit = style.getPropertyValue('--text-secondary').trim() || '#8b949e';
  themeAccent = style.getPropertyValue('--accent-cyan').trim() || '#00d4aa';
}

function onWindowResize() {
  resize();
  markDirty();
}

export function unmount() {
  themeObserver?.disconnect();
  themeObserver = null;
  if (rafId) cancelAnimationFrame(rafId);
  rafId = null;
  dirty = false;
  scene = { nodes: [], edges: [] };
  if (resizeObserver) {
    resizeObserver.disconnect();
    resizeObserver = null;
  } else {
    window.removeEventListener("resize", onWindowResize);
  }
  if (canvasEl) {
    canvasEl.removeEventListener("pointerdown", onPointerDown);
    canvasEl.removeEventListener("pointermove", onPointerMove);
    canvasEl.removeEventListener("pointerup", onPointerUp);
    canvasEl.removeEventListener("pointercancel", onPointerCancel);
    canvasEl.removeEventListener("contextmenu", onContextMenu);
    canvasEl.remove();
    canvasEl = null;
    ctx = null;
  }
  clearTimeout(longPressTimer);
  longPressTimer = null;
  host = null;
  getTransformFn = null;
  getSelectedIdFn = null;
  onTapFn = null;
  onLongPressFn = null;
}

export function isMounted() {
  return !!canvasEl;
}

/**
 * Replace the painted scene. Called by the mindmap's doRender after the
 * layout pass collected node/edge records in canvas mode.
 * @param {{nodes: Array, edges: Array}} next
 */
export function setScene(next) {
  scene = next || { nodes: [], edges: [] };
  markDirty();
}

/**
 * World-space bounding box of the scene, for fit-to-view (there are no DOM
 * nodes to measure in canvas mode). Null when empty.
 */
export function getContentBounds() {
  if (!scene.nodes.length) return null;
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (const n of scene.nodes) {
    minX = Math.min(minX, n.x - n.w / 2);
    minY = Math.min(minY, n.y - n.h / 2);
    maxX = Math.max(maxX, n.x + n.w / 2);
    maxY = Math.max(maxY, n.y + n.h / 2);
  }
  return { minX, minY, maxX, maxY };
}

export function markDirty() {
  if (!canvasEl) return;
  dirty = true;
  if (rafId) return;
  rafId = requestAnimationFrame(() => {
    rafId = null;
    if (dirty) {
      dirty = false;
      drawFrame();
    }
  });
}

// ============================================================================
// PAINT
// ============================================================================

function resize() {
  if (!canvasEl || !host) return;
  const rect = host.getBoundingClientRect();
  const dpr = Math.min(window.devicePixelRatio || 1, DPR_CAP);
  const w = Math.max(1, Math.round(rect.width * dpr));
  const h = Math.max(1, Math.round(rect.height * dpr));
  if (canvasEl.width !== w) canvasEl.width = w;
  if (canvasEl.height !== h) canvasEl.height = h;
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
}

function drawFrame() {
  if (!ctx || !canvasEl) return;
  const rect = host.getBoundingClientRect();
  const cssW = rect.width;
  const cssH = rect.height;
  ctx.clearRect(0, 0, cssW, cssH);

  const tx = getTransformFn?.() || { zoom: 1, panX: 0, panY: 0 };
  const z = tx.zoom || 1;
  const px = tx.panX || 0;
  const py = tx.panY || 0;

  if (!scene.nodes.length) {
    ctx.font = "600 15px system-ui, sans-serif";
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillStyle = "rgba(160,160,160,0.8)";
    ctx.fillText(t("views.noItems"), cssW / 2, cssH / 2);
    return;
  }

  // Edges first (under the nodes), painter order as collected.
  ctx.lineCap = "round";
  for (const e of scene.edges) {
    const x1 = px + e.px * z;
    const y1 = py + e.py * z;
    const x2 = px + e.x * z;
    const y2 = py + e.y * z;
    // Cull edges whose bounding box misses the viewport.
    if (Math.max(x1, x2) < -20 || Math.min(x1, x2) > cssW + 20) continue;
    if (Math.max(y1, y2) < -20 || Math.min(y1, y2) > cssH + 20) continue;
    const dx = x2 - x1;
    ctx.beginPath();
    ctx.moveTo(x1, y1);
    ctx.bezierCurveTo(x1 + dx * 0.5, y1, x1 + dx * 0.5, y2, x2, y2);
    ctx.strokeStyle = safeColor(e.color, "#888");
    ctx.globalAlpha = e.dim ? 0.15 : 0.8;
    ctx.lineWidth = Math.max(0.75, 3 * z);
    ctx.stroke();
  }
  ctx.globalAlpha = 1;

  const selectedId = getSelectedIdFn?.();
  for (const n of scene.nodes) {
    paintNode(n, cssW, cssH, px, py, z, n.id === selectedId);
  }
}

function paintNode(n, cssW, cssH, panX, panY, zoom, isSelected) {
  const sx = panX + n.x * zoom;
  const sy = panY + n.y * zoom;
  const sw = (n.bodyW ?? n.w) * zoom;
  const sh = (n.bodyH ?? n.h) * zoom;

  if (sx + n.w * zoom / 2 < -20 || sy + n.h * zoom / 2 < -20) return;
  if (sx - n.w * zoom / 2 > cssW + 20 || sy - n.h * zoom / 2 > cssH + 20) return;

  const dimmed = n.spotlight === "dim";
  ctx.globalAlpha = dimmed ? 0.18 : 1;

  const color = safeColor(n.color, "#4a5568");
  const radius = Math.min(25 * zoom, sh / 2);

  ctx.beginPath();
  ctx.roundRect(sx - sw / 2, sy - sh / 2, sw, sh, radius);
  ctx.fillStyle = color;
  ctx.fill();
  ctx.lineWidth = Math.max(0.5, 2.5 * zoom);
  ctx.strokeStyle = "rgba(255,255,255,0.9)";
  ctx.stroke();

  if (n.routine) drawRoutineOrbit(ctx, sx, sy, n.bodyW ?? n.w, n.bodyH ?? n.h, 25, themeOrbit, zoom);

  // Selection: accent outer ring (nodes already stroke white, so white
  // can't signal selection like it does for bubbles). Fixed minimum so it
  // reads even when a 715-node fit makes the node itself a sliver.
  if (isSelected) {
    const pad = Math.max(3, 4 * zoom);
    ctx.beginPath();
    ctx.roundRect(sx - sw / 2 - pad, sy - sh / 2 - pad, sw + pad * 2, sh + pad * 2, radius + pad);
    ctx.lineWidth = Math.max(2, 2.5 * zoom);
    ctx.strokeStyle = themeAccent;
    ctx.stroke();
  }

  // Title: white, weight 600 — same as .mm-title. Skip when unreadably small.
  const fontSize = (n.isRoot ? 16 : n.isLeaf ? 12 : 13) * zoom;
  if (fontSize >= 5 && n.text) {
    ctx.font = `600 ${fontSize}px system-ui, sans-serif`;
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillStyle = "#fff";
    const lineHeight = fontSize * 1.4;
    const lines = n.wrapped;
    const startY = sy - ((lines.length - 1) * lineHeight) / 2;
    const maxTextW = sw - 12 * zoom;
    for (let i = 0; i < lines.length; i++) {
      const line = fitLine(lines[i], maxTextW);
      const ly = startY + i * lineHeight;
      ctx.fillText(line, sx, ly);
      if (n.status === "done") {
        const w = ctx.measureText(line).width;
        ctx.save();
        ctx.strokeStyle = "#fff";
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.moveTo(sx - w / 2, ly);
        ctx.lineTo(sx + w / 2, ly);
        ctx.stroke();
        ctx.restore();
      }
    }
  }

  // Collapsed hint: "+" at the node's outer edge, like the DOM toggle.
  if (n.collapsed && sh >= 10) {
    const fs = Math.max(8, Math.min(14, sh * 0.5));
    ctx.font = `700 ${fs}px system-ui, sans-serif`;
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillStyle = "rgba(255,255,255,0.95)";
    ctx.fillText("+", sx + sw / 2 - fs * 0.6, sy - sh / 2 + fs * 0.6);
  }

  // Notes dot (bottom-right corner), stand-in for the DOM notes badge.
  if (n.hasNotes && sh >= 12) {
    ctx.beginPath();
    ctx.arc(sx + sw / 2 - 4 * zoom, sy + sh / 2 - 4 * zoom, Math.max(1.5, 2.5 * zoom), 0, Math.PI * 2);
    ctx.fillStyle = "rgba(255,255,255,0.9)";
    ctx.fill();
  }

  ctx.globalAlpha = 1;
}

/** Ellipsise a single line to maxWidth under the CURRENT ctx.font. */
function fitLine(text, maxWidth) {
  if (ctx.measureText(text).width <= maxWidth) return text;
  let lo = 0;
  let hi = text.length;
  while (lo < hi) {
    const mid = (lo + hi + 1) >> 1;
    if (ctx.measureText(text.slice(0, mid) + "…").width <= maxWidth) lo = mid;
    else hi = mid - 1;
  }
  return lo > 0 ? text.slice(0, lo) + "…" : "";
}

// ============================================================================
// TAP
// ============================================================================

let downX = 0;
let downY = 0;
let downTime = 0;
let longPressTimer = null;
let longPressFired = false;

const LONG_PRESS_MS = 500;
const LONG_PRESS_SLOP_PX = 10;

/** Topmost node under a canvas-local point (last drawn wins — children are
 *  collected after their parents). Returns the node id or null. */
function hitTest(pxp, pyp) {
  const tx = getTransformFn?.() || { zoom: 1, panX: 0, panY: 0 };
  const z = tx.zoom || 1;
  for (let i = scene.nodes.length - 1; i >= 0; i--) {
    const n = scene.nodes[i];
    const sx = (tx.panX || 0) + n.x * z;
    const sy = (tx.panY || 0) + n.y * z;
    const sw = n.w * z;
    const sh = n.h * z;
    if (pxp >= sx - sw / 2 && pxp <= sx + sw / 2 && pyp >= sy - sh / 2 && pyp <= sy + sh / 2) {
      return n.id;
    }
  }
  return null;
}

/**
 * After a long-press the browser still synthesizes a click when the finger
 * lifts — by then the node panel covers the canvas, so that ghost click
 * would land on whatever panel control renders under the finger. Swallow
 * the first click arriving within a short window after the lift.
 * (Mirrored in interfaces/classic/views/bubble/canvas-renderer.js.)
 */
function suppressPostLongPressClick() {
  const arm = () => {
    const swallow = (ev) => {
      ev.stopPropagation();
      ev.preventDefault();
      window.removeEventListener("click", swallow, true);
    };
    window.addEventListener("click", swallow, true);
    setTimeout(() => window.removeEventListener("click", swallow, true), 150);
  };
  window.addEventListener("pointerup", arm, { capture: true, once: true });
  setTimeout(() => window.removeEventListener("pointerup", arm, true), 3000);
}

function onPointerDown(e) {
  clearTimeout(longPressTimer);
  longPressTimer = null;
  // Second finger = pinch, not a press.
  if (!e.isPrimary) return;
  downX = e.clientX;
  downY = e.clientY;
  downTime = Date.now();
  longPressFired = false;
  if (!onLongPressFn) return;
  longPressTimer = setTimeout(() => {
    longPressTimer = null;
    if (!canvasEl) return;
    const rect = canvasEl.getBoundingClientRect();
    const id = hitTest(downX - rect.left, downY - rect.top);
    if (id) {
      longPressFired = true;
      try { navigator.vibrate?.(15); } catch { /* unsupported */ }
      suppressPostLongPressClick();
      onLongPressFn(id);
    }
  }, LONG_PRESS_MS);
}

function onPointerMove(e) {
  if (!longPressTimer || !e.isPrimary) return;
  if (Math.hypot(e.clientX - downX, e.clientY - downY) > LONG_PRESS_SLOP_PX) {
    clearTimeout(longPressTimer);
    longPressTimer = null;
  }
}

function onPointerCancel() {
  clearTimeout(longPressTimer);
  longPressTimer = null;
}

function onContextMenu(e) {
  e.preventDefault();
}

function onPointerUp(e) {
  clearTimeout(longPressTimer);
  longPressTimer = null;
  if (longPressFired) {
    // The hold already opened the node properties — don't also select-tap.
    longPressFired = false;
    return;
  }
  if (!canvasEl || !onTapFn) return;
  const moved = Math.hypot(e.clientX - downX, e.clientY - downY);
  if (moved > 8 || Date.now() - downTime > 600) return;

  const rect = canvasEl.getBoundingClientRect();
  const id = hitTest(e.clientX - rect.left, e.clientY - rect.top);
  // Tap on empty space clears the selection (null) — matches the outline's
  // tap-to-deselect and lets the + button target the root again.
  onTapFn(id || null);
  markDirty();
}
